import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { partidasAPI } from '../services/api';
import { formatVsPar, parDelCampo, parPersonal } from '../utils/handicap';
import './Results.css';

function Results() {
  const navigate = useNavigate();
  const { codigo } = useParams();
  const [estadisticas, setEstadisticas] = useState([]);
  const [partida, setPartida] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [codigo]);

  const loadResults = async () => {
    try {
      const [statsResponse, partidaResponse] = await Promise.all([
        partidasAPI.getEstadisticas(codigo),
        partidasAPI.getByCode(codigo)
      ]);

      setEstadisticas(statsResponse.data);
      setPartida(partidaResponse.data);
    } catch (err) {
      console.error('Error al cargar resultados:', err);
    } finally {
      setLoading(false);
    }
  };

  const compartirResultados = () => {
    const texto = generarTextoResultados();

    if (navigator.share) {
      navigator.share({
        title: 'Resultados de la partida',
        text: texto
      }).catch(err => console.log('Error al compartir:', err));
    } else {
      navigator.clipboard.writeText(texto).then(() => {
        alert('Resultados copiados al portapapeles');
      });
    }
  };

  // Las partidas anteriores al reparto por hoyo no tienen las columnas nuevas:
  // se reconstruyen a partir del par del campo y del handicap del jugador.
  const conAjuste = (jugador) => {
    const parCampo = parDelCampo(partida?.hoyos);
    return {
      ...jugador,
      par_personal: jugador.par_personal ?? parPersonal(parCampo, jugador.handicap),
      vs_par: jugador.vs_par ?? Math.round(Number(jugador.puntuacion_neta) - parCampo)
    };
  };

  const generarTextoResultados = () => {
    let texto = `🏌️ Resultados - ${partida?.campo_nombre}\n\n`;
    texto += '🏆 Clasificación:\n';

    estadisticas.map(conAjuste).forEach((jugador, index) => {
      const emoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`;
      texto += `${emoji} ${jugador.nombre}\n`;
      texto += `   Golpes: ${jugador.total_golpes} | Neto: ${jugador.puntuacion_neta}`;
      texto += ` | ${formatVsPar(jugador.vs_par)} sobre su par (${jugador.par_personal})\n`;
      if (jugador.puntos_stableford != null) {
        texto += `   Stableford: ${jugador.puntos_stableford} pts\n`;
      }
      if (jugador.total_drops > 0) texto += `   Drops: ${jugador.total_drops}\n`;
      if (jugador.total_mulligans > 0) texto += `   Mulligans: ${jugador.total_mulligans}\n`;
      texto += '\n';
    });

    texto += '\n⛳ ¡Jugado con MisGolpes!';
    return texto;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando resultados...</p>
      </div>
    );
  }

  const claseVsPar = (valor) => (valor < 0 ? 'bajo-par' : valor > 0 ? 'sobre-par' : 'en-par');

  return (
    <div className="results">
      <div className="results-header">
        <div className="trophy-icon">🏆</div>
        <h1>Partida Finalizada</h1>
        <p>{partida?.campo_nombre}</p>
      </div>

      <div className="container">
        <div className="card fade-in">
          <h2 className="results-title">Clasificación Final</h2>

          <div className="podium">
            {estadisticas.slice(0, 3).map(conAjuste).map((jugador, index) => (
              <div key={jugador.jugador_id} className={`podium-position pos-${index + 1}`}>
                <div className="podium-medal">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </div>
                <div className="podium-name">{jugador.nombre}</div>
                <div className="podium-score">{formatVsPar(jugador.vs_par)}</div>
                <div className="podium-detalle">{jugador.total_golpes} golpes</div>
              </div>
            ))}
          </div>

          <div className="results-list">
            {estadisticas.map(conAjuste).map((jugador, index) => (
              <div key={jugador.jugador_id} className="result-item">
                <div className="result-position">
                  {index + 1}
                  {index === 0 && <span className="winner-star">⭐</span>}
                </div>
                <div className="result-info">
                  <div className="result-name">{jugador.nombre}</div>
                  <div className="result-details">
                    <div className="detail-item">
                      <span className="detail-label">Golpes:</span>
                      <span className="detail-value">{jugador.total_golpes}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Handicap:</span>
                      <span className="detail-value">{jugador.handicap}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Su par:</span>
                      <span className="detail-value">{jugador.par_personal}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Neto:</span>
                      <span className="detail-value strong">{jugador.puntuacion_neta}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Sobre su par:</span>
                      <span className={`detail-value strong vs-par ${claseVsPar(jugador.vs_par)}`}>
                        {formatVsPar(jugador.vs_par)}
                      </span>
                    </div>
                    {jugador.puntos_stableford != null && (
                      <div className="detail-item">
                        <span className="detail-label">Stableford:</span>
                        <span className="detail-value">{jugador.puntos_stableford} pts</span>
                      </div>
                    )}
                  </div>
                  {(jugador.total_drops > 0 || jugador.total_mulligans > 0) && (
                    <div className="result-extras">
                      {jugador.total_drops > 0 && (
                        <span className="extra-badge drops">Drops: {jugador.total_drops}</span>
                      )}
                      {jugador.total_mulligans > 0 && (
                        <span className="extra-badge mulligans">Mulligans: {jugador.total_mulligans}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button className="btn btn-primary btn-full share-btn" onClick={compartirResultados}>
            📤 Compartir Resultados
          </button>

          <button
            className="btn btn-secondary btn-full"
            onClick={() => navigate('/')}
            style={{ marginTop: '12px' }}
          >
            Nueva Partida
          </button>
        </div>
      </div>
    </div>
  );
}

export default Results;
